import { configureStore, createSlice } from "@reduxjs/toolkit";
import { getSlug, splitElementKey } from "../lib/helpers";
import { DEFAULT_LAYOUT_CONFIG } from "../config";
import {
  DEFAULT_CLASSES,
  MULTI_PAGE_SEPARATOR,
  LISTICLE_SEPARATOR,
  INITIAL_ZOOM_LEVEL,
} from "../lib/constants";
import { getUserId, updateConfigInFirestore } from "./firebase";
import { generatePlatforms } from "../lib/helpers";
import { getAuth, signOut } from "firebase/auth";
import _ from "lodash";
import shortid from "shortid";
import dayjs from "dayjs";
import { mixpanelClear } from "../lib/tracking";
import GLOBAL_TEMPLATES from "../lib/templates";

const CONTENT = `
### A Markdown-based Social Media Post Generator

What You Can Do

✍️ **Markdown Support** - Write content using simple Markdown formatting for clean and fast editing.

🎯 **Auto Layout** - Your content is automatically centered and structured beautifully.
No manual alignment needed.

📄 **Multi-page Posts** - Create carousel-style posts using: 
< page > to split content into multiple pages.

📋 **Listicle Posts** - Use line breaks to automatically generate listicle-style layouts.

🎨 **Templates & Variants** - Customize tags like strong, code, and more — or save complete page templates for reuse.

📤 **Easy Export** - Export your posts instantly without dealing with design tools.

Happy Posting 🎉
`;

// const CONTENT = `
// # Heading 1
// ## Heading 2
// ### Heading 3
// #### Heading 4
// ##### Heading 5
// ###### Heading 6
// ---
// Ordered list
// 1. Item A
// 2. Item B
// 3. Item C
// 4. Item D
// ---
// Unordered list
// - Item A
// - Item B
// - Item C
// - Item D
// `;

const DEFAULT_POST_DATA = {
  title: "Welcome to Grid ✨",
  content: CONTENT,
  //   title: "Did you know the `world’s` first website is still live? 🌍💻",
  //   content: `Tim Berners-Lee launched the **World Wide Web Project** at CERN, giving birth to the internet as we know it.
  // The original site, hosted at [**info.cern.ch**](https://info.cern.ch/), was the first step toward a digital revolution.
  //     `,
  brand: "brand.name.co",
  files: [],
};

const postConfig = {
  data: DEFAULT_POST_DATA,
  globalProperties: DEFAULT_CLASSES,
  localProperties: {},
  propertyType: "global",
  pages: [],
  activePlatform: "instagram",
  postVariant: "default",
  zoomLevel: INITIAL_ZOOM_LEVEL,
  view: "col",
  layoutConfig: DEFAULT_LAYOUT_CONFIG,
  apiConfig: {
    endpoint: "",
    queryString: "",
    cursor: "",
    responseObj: "data",
    titleMapping: "title",
    contentMapping: "content",
    cursorMapping: "",
  },
};

const userPreferences = {
  totalExports: 0,
  exportId: 1,
};

const initialState = {
  loading: true,
  filename: "",
  notification: null,
  templates: [],
  bgFiles: [],
  activeTemplateId: null,
  activeSidebarPanels: [
    "general",
    "background",
    "layout",
    "font",
    "border",
    "spacing",
    "effects",
  ],
  activeSidebarTab: "data",
  domOutline: [],
  ...postConfig,
  ...userPreferences,
};

const isMultipageOrListicle = (postVariant, content) => {
  const isMultipage =
    postVariant === "multipage" && content.includes(MULTI_PAGE_SEPARATOR);
  const isListicle =
    postVariant === "listicle" && content.includes(LISTICLE_SEPARATOR);

  return isMultipage || isListicle;
};

const rawSlice = createSlice({
  name: "grid",
  initialState,
  reducers: {
    setData: (state, action) => {
      const data = { ...state.data, ...action.payload };
      if (data.title) {
        state.filename = getSlug(data.title);
      }
      state.data = data;
    },
    setView: (state, action) => {
      state.view = action.payload;
    },
    setPostVariant: (state, action) => {
      const shouldUpdateZoom = state.postVariant !== action.payload;
      state.postVariant = action.payload;

      state.data = _.pick(state.data, Object.keys(DEFAULT_LAYOUT_CONFIG));
      state.pages = generatePlatforms(state.activePlatform);

      if (isMultipageOrListicle(state.postVariant, state.data.content)) {
        const isListicle = state.postVariant === "listicle";
        const pageContent = state.data.content
          .trim()
          .split(isListicle ? LISTICLE_SEPARATOR : MULTI_PAGE_SEPARATOR)
          .map((content) => content.trim())
          .filter((content) => content.length);

        const pageContentMap = {};

        pageContent.forEach((page, index) => {
          const pageId = `content_#${index + 1}`;
          pageContentMap[pageId] = page;
        });

        const pageIds = Object.keys(pageContentMap);

        const { platform } = state.pages[0];

        const firstPage = generatePlatforms(platform, {
          title: "title",
          content: isListicle ? null : "content_#1",
        });

        const otherPages = (isListicle ? pageIds : pageIds.slice(1)).map(
          (id, idx) =>
            generatePlatforms(platform, {
              title: null,
              content: id,
            })[0],
        );

        const pages = _.flatten([firstPage, otherPages]);

        const finalPages = pages.map((page, idx) => ({
          ...page,
          pagination: `${idx + 1}/${pages.length}`,
        }));

        state.pages = finalPages;
        state.data = { ...state.data, ...pageContentMap };
        if (shouldUpdateZoom && pages.length > 1) {
          state.zoomLevel = 0.4;
          state.view = "row";
        }
      } else {
        state.zoomLevel = INITIAL_ZOOM_LEVEL;
        state.view = "col";
      }
    },
    setSelectedPlatforms: (state, action) => {
      state.activePlatform = action.payload;
      state.pages = generatePlatforms(state.activePlatform);
    },
    setFilename: (state, action) => {
      state.filename = action.payload;
    },
    setPropertyType: (state, action) => {
      state.propertyType = action.payload;
    },
    setGlobalProperties: (state, action) => {
      state.globalProperties = action.payload;
    },
    setLocalProperties: (state, action) => {
      state.localProperties = action.payload;
    },
    setSelectedFiles: (state, action) => {
      state.data.files = action.payload;
    },
    setBgFiles: (state, action) => {
      state.bgFiles = action.payload;
    },
    setNotification: (state, action) => {
      state.notification = action.payload;
    },
    setShowDemo: (state, action) => {
      state.showDemo = action.payload;
    },
    setActiveSidebarPanels: (state, action) => {
      state.activeSidebarPanels = action.payload;
    },
    setActiveSidebarTab: (state, action) => {
      state.activeSidebarTab = action.payload;
    },
    saveElementVariant: (state, action) => {
      const { element } = splitElementKey(action.payload);
      if (element) {
        state.layoutConfig[element].variants.push({
          id: shortid(),
          createdAt: dayjs().format("YYYY-MM-DD"),
          properties: {
            ..._.get(state.globalProperties, element, {}),
            ..._.get(state.localProperties, element, {}),
          },
        });
        state.notification = `Variant saved: ${action.payload}`;
      }
    },
    applyNextElementVariant: (state, action) => {
      const { element } = splitElementKey(action.payload);
      if (element) {
        const variants = _.get(state.layoutConfig, [element, "variants"], []);
        if (variants.length > 0) {
          const variantIdx = _.get(
            state.layoutConfig,
            [element, "activeVariant"],
            null,
          );
          const nextVariantIdx =
            variantIdx === null ? 0 : (variantIdx + 1) % variants.length;

          _.set(state.layoutConfig, [element, "activeVariant"], nextVariantIdx);

          const nextVariant = variants[nextVariantIdx];
          state.globalProperties[element] = nextVariant.properties;
        }
      }
    },
    resetElement: (state, action) => {
      const { element } = splitElementKey(action.payload);

      state.localProperties[action.payload] = {};
      state.globalProperties[element] = _.get(DEFAULT_CLASSES, element, {});
    },
    resetState: (state) => {
      Object.assign(state, _.omit(initialState, Object.keys(userPreferences)));
      state.pages = generatePlatforms(state.activePlatform);
    },
    incrementExportId: (state) => {
      state.exportId++;
    },
    setExportId: (state, action) => {
      state.exportId = action.payload;
    },

    incrementTotalExports: (state) => {
      state.totalExports++;
    },
    setDomOutline: (state, action) => {
      state.domOutline = action.payload;
    },
    updateDataConfig: (state, { payload }) => {
      const { key, property } = payload;

      if (property === "visible")
        _.set(
          state.layoutConfig,
          [key, "visible"],
          !_.get(state.layoutConfig, [key, "visible"]),
        );
    },
    setInitialState: (state, action) => {
      Object.assign(state, {
        ...initialState,
        ...action.payload,
      });
      state.pages = generatePlatforms(state.activePlatform);
      state.loading = false;
    },
    setZoomLevel: (state, action) => {
      const newValue =
        Math.round(
          (action.payload === "+"
            ? Number(state.zoomLevel) + 0.1
            : Number(state.zoomLevel) - 0.1) * 10,
        ) / 10;
      if (newValue >= 0.1 && newValue <= 1) state.zoomLevel = newValue;
    },
    logout: (state, action) => {
      const auth = getAuth();
      signOut(auth)
        .then(() => {
          state.loading = true;
          Object.assign(state, initialState);
          mixpanelClear();
        })
        .catch((error) => {
          console.log("error::-", error);
          // state.notification = "Logout failed: " + error.message;
        });
    },
    saveTemplate: (state, action) => {
      if (!state.templates) state.templates = [];
      const label = action.payload || `Template ${state.templates.length + 1}`;
      state.templates.push({
        id: shortid(),
        createdAt: dayjs().format("YYYY-MM-DD"),
        updatedAt: dayjs().format("YYYY-MM-DD"),
        label,
        data: _.pick(state, Object.keys(postConfig)),
      });
      state.notification = `"${label}" template saved.`;
      state.activeTemplateId = state.templates.slice(-1)[0].id;
    },
    updateTemplate: (state) => {
      state.templates = state.templates.map((session) => {
        if (session.id === state.activeTemplateId) {
          return {
            ...session,
            updatedAt: dayjs().format("YYYY-MM-DD"),
            data: _.pick(state, Object.keys(postConfig)),
          };
        }
        return session;
      });
      state.notification = `Template updated.`;
    },
    setSelectedTemplate: (state, action) => {
      const session = [...GLOBAL_TEMPLATES, ...state.templates].find(
        (s) => s.id === action.payload,
      );
      if (session) {
        Object.assign(state, {
          ...state,
          ...session.data,
        });
        state.activeTemplateId = session.id;
      }
    },
    setApiConfig: (state, action) => {
      state.apiConfig = { ...state.apiConfig, ...action.payload };
    },
  },
});

const setDataThunk = (data) => async (dispatch, getState) => {
  const { grid } = getState();
  await dispatch(setData(data));

  if (
    grid.postVariant === "multipage" ||
    data.content?.includes(MULTI_PAGE_SEPARATOR)
  ) {
    dispatch(setPostVariant("multipage"));
  } else if (grid.postVariant === "listicle") {
    dispatch(setPostVariant("listicle"));
  }
};

const store = configureStore({
  reducer: {
    grid: rawSlice.reducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware(),
});

store.subscribe(() => {
  const state = store.getState();

  if (state.loading || !getUserId()) return;

  updateConfigInFirestore({
    ..._.pick(state.grid, [
      ...Object.keys(postConfig),
      ...Object.keys(userPreferences),
    ]),
    data: {
      ..._.pick(state.grid.data, Object.keys(DEFAULT_POST_DATA)),
    },
  });
});

export const {
  setData,
  setView,
  setPostVariant,
  setZoomLevel,
  setSelectedPlatforms,
  setFilename,
  setPropertyType,
  setGlobalProperties,
  setLocalProperties,
  setSelectedFiles,
  resetState,
  incrementExportId,
  setExportId,
  saveElementVariant,
  applyNextElementVariant,
  setNotification,
  setInitialState,
  logout,
  incrementTotalExports,
  updateDataConfig,
  resetElement,
  setShowDemo,
  saveTemplate,
  setSelectedTemplate,
  updateTemplate,
  setApiConfig,
  setActiveSidebarPanels,
  setActiveSidebarTab,
  setBgFiles,
  setDomOutline,
} = rawSlice.actions;

export { initialState, setDataThunk };

export default store;
